import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, shareReplay } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ApiService } from './api.service';

export interface Property {
  code: string;
  name: string;
}

/**
 * Service for managing Valamar properties/hotels
 * Properties are cached locally for performance
 */
@Injectable({
  providedIn: 'root'
})
export class PropertyService {
  private propertiesSubject: BehaviorSubject<Property[]> = new BehaviorSubject<Property[]>([]);
  public properties$: Observable<Property[]> = this.propertiesSubject.asObservable();
  private propertiesCache: Property[] | null = null;

  constructor(private apiService: ApiService) {}

  /**
   * Get list of all properties (with caching)
   * GET /api/v2/Property
   */
  getProperties(): Observable<Property[]> {
    if (this.propertiesCache) {
      return of(this.propertiesCache);
    }

    return this.apiService.get<Property[]>('/api/v1/Property').pipe(
      tap(properties => {
        this.propertiesCache = properties;
        this.propertiesSubject.next(properties);
      }),
      shareReplay(1),
      catchError(error => {
        console.error('Failed to load properties:', error);
        return of([]);
      })
    );
  }

  /**
   * Get property by code
   */
  getPropertyByCode(code: string): Property | undefined {
    return this.propertiesCache?.find(p => p.code === code);
  }

  /**
   * Clear properties cache
   */
  clearCache(): void {
    this.propertiesCache = null;
    this.propertiesSubject.next([]);
  }

  /**
   * Refresh properties from API
   */
  refreshProperties(): Observable<Property[]> {
    this.clearCache();
    return this.getProperties();
  }
}
